"use client"

import {
  Box,
  Field as ChakraField,
  Heading,
  Button,
  Icon,
  Text,
} from "@chakra-ui/react"
import { HStack, VStack } from "@chakra-ui/react/stack"
import { FormEvent, useState } from "react"

import { Field } from "@/components/ui/field"
import { PasswordInput } from "@/components/ui/password-input"
import { ContentBlockRenderer } from "@/components/ContentBlockRenderer"
import { LockIcon, UnlockIcon } from "@/components/icons"
import { ContentBlockWrapper } from "@/types/database"

interface ProtectedCaseStudyContentProps {
  onUnlocked?: () => void
  slug: string
}

interface UnlockResponse {
  contentBlocks?: ContentBlockWrapper[]
  message?: string
  success: boolean
}

const ProtectedCaseStudyContent = ({
  onUnlocked,
  slug,
}: ProtectedCaseStudyContentProps) => {
  const [contentBlocks, setContentBlocks] = useState<ContentBlockWrapper[]>([])
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [password, setPassword] = useState("")

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError("")
    setLoading(true)

    try {
      const response = await fetch(
        `/api/case-study/${encodeURIComponent(slug)}/content`,
        {
          body: JSON.stringify({ password }),
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
        }
      )
      const data = (await response.json()) as UnlockResponse

      if (!response.ok || !data.success) {
        setError(data.message ?? "Unable to unlock this case study.")
        return
      }

      setContentBlocks(data.contentBlocks ?? [])
      setPassword("")
      onUnlocked?.()
    } catch {
      setError("Unable to unlock this case study.")
    } finally {
      setLoading(false)
    }
  }

  if (contentBlocks.length > 0) {
    return <ContentBlockRenderer contentBlocks={contentBlocks} />
  }

  return (
    <VStack
      gap={0}
      mx="auto"
      width="100%"
      alignItems="center"
      className="protected-section"
      borderBottom={"1px solid {colors.border}"}
    >
      <VStack
        borderLeft={"1px solid {colors.border}"}
        borderRight={"1px solid {colors.border}"}
        gap={0}
        w="full"
        maxW={"8xl"}
        px={{ base: 8, md: 10, lg: 24, xl: 40 }}
        py={{ base: 8, md: 10, lg: 24 }}
      >
        <form onSubmit={handleSubmit}>
          <Box
            border="1px solid {colors.border}"
            borderRadius="l2"
            maxW="md"
            p={{ base: 5, md: 6 }}
          >
            <VStack alignItems="stretch" gap={5}>
              <VStack alignItems="flex-start" gap={2}>
                <HStack>
                  <Icon as={LockIcon} size="md" color="orange.500" />
                  <Heading as="h2" color="fg" fontSize="xl" fontWeight="600">
                    Protected case study
                  </Heading>
                </HStack>
                <Text color="fg.muted" fontSize="sm">
                  Enter the password to view the case study content.
                </Text>
              </VStack>
              <Field invalid={Boolean(error)} label="Password" required>
                <PasswordInput
                  autoComplete="current-password"
                  disabled={loading}
                  onChange={(event) => setPassword(event.currentTarget.value)}
                  value={password}
                />
                {error && (
                  <ChakraField.ErrorText aria-live="polite">
                    {error}
                  </ChakraField.ErrorText>
                )}
              </Field>
              <Button
                alignSelf="flex-start"
                disabled={password.length === 0}
                loading={loading}
                type="submit"
                variant="solid"
                width="full"
              >
                <UnlockIcon />
                Unlock
              </Button>
            </VStack>
          </Box>
        </form>
      </VStack>
    </VStack>
  )
}

export default ProtectedCaseStudyContent
